<template>
  <ion-page id="legacy-play-collection-page">
    <ion-content>

    <div v-show="showTour">
        <Tour  :tour="tourInfo" :tourTrigger="tourTrigger" @tour-done="tourDone" />
    </div>

    <div id="the_board" :style="styleBoard">
      <CMChessboard  :fen="forceFen" :validMoves="availableMoves" :orientation="orientation" 
                :boardSize="boardSize" :hint="hintFen" :animateOnePly="animateFen"
                @board-move="boardMove"/>
    </div>
    <div id="the_movelist" :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleMoveList">
      <MoveList :resultsData="moveHistoryStack" :isClickable=false :eighty = "eighty"
                    :scrollToBottomTrigger="scrollToBottomTrigger" />
    </div>

    <div id="the_movehistory" :style="styleMoveHistory" :class="{ 'text-dark' : isDark,'text-light' : !isDark }">
      <MoveHistory :moveHistoryStack="moveHistoryStack" 
                    :noAvailableMovesFlag="noAvailableMovesFlag"
                      :trigger="moveHistoryTrigger" />
    </div>

    </ion-content>
    <Toolbar id="the_toolbar" :ignoreClicks="showTour" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
  </ion-page>

</template>

<script setup>

  import { IonItem, IonList, IonButton, IonRange,IonPage, IonContent, modalController } from '@ionic/vue';

  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'

  import CMChessboard from '@/components/CMChessboard.vue'
  import { t } from '@/composables/translateText.js'

  import MoveList from '@/components/MoveList.vue'
  import Toolbar from '@/components/Toolbar.vue'
  import MoveHistory from '@/components/MoveHistory.vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'

  import ModalGenericSingleList from '@/components/ModalGenericSingleList.vue'

  import { onMounted, onBeforeUnmount } from 'vue'
  import { ref, watch } from 'vue'
  import { useGetData } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation} from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import Tour from '@/components/Tour.vue'

  var hintIndex = 0

  const windowSizeStore = useWindowSizeStore()
  const noAvailableMovesFlag = ref(0)
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  //console.log("**** explorer initial screensize", windowSizeRef.value)
  const boardSize = ref(100)

  var blankAppendTrigger = ""
  const connectionStore = useConnectionStore()

  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const tourOption = connectionStore.getTourOption
  const showTour = ref((tourOption == "show"))
  
  var initialFenid4 = "147869767"
  var initialFenid8 = "1"

  var ratingFilter = "all"

  const TOOLBAR_BACK = 0
  const TOOLBAR_REWIND = 1
  const TOOLBAR_FLIP = 2
  const TOOLBAR_HINT = 3
  const TOOLBAR_RATING = 4

  const TOOLBAR_RATING_BASE = 10
  const ratingStrings = ["bottom","middle","top","all"]

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const MOVEHISTORY_FONTSIZE = 16
  const MOVEHISTORY_HEIGHT =  MOVEHISTORY_FONTSIZE * 1.2 * 2 + 4

  const toolbarElements = ref([
    { index : TOOLBAR_BACK, label : "<" , disabled : true, collapse:false},
    { index : TOOLBAR_REWIND, label : "<<" , disabled : true, collapse:false},
    { index : TOOLBAR_FLIP, label : t("Flip") ,disabled : false, collapse:true},
    { index : TOOLBAR_HINT, label : t("Hint") ,disabled : false, collapse:false},
    { index : TOOLBAR_RATING, label : t("Rating Filter") , disabled : false, collapse:false }
  ])

  const optionDisplayArray = [
     { index : 0, name : t("below") + " 1400", id : "bottom" , radio : "bottom" },
     { index : 0, name : "1400-1700", id : "middle" , radio : "middle" },
     { index : 0, name : t("above") + " 1700", id : "top" , radio : "top" },
     { index : 0, name : t("All"), id : "all" , radio : "all" }
      ]
  var optionDisplaySelected = ratingFilter

  // tour stuff
  const tourInfo = ref({
    id : "opening_collection",
    video : "https://www.chessvis.com/videos/play_against_game_collection.mp4",
    elements : [
      { id: "", title: "Play Against Game Collection", text : "Experience the opening moves that you'll see in real games.<br><br><i>Note: the video is a bit out-of-date and be updated soon."},
      { id: "", title: "Focus on What You'll See On The Board", text : "Filter the game collection based on your rating bracket and Chessvis will take on the role of your opponent and play moves from that filtered bracket.<br><br>The opponent moves will be randomly selected based on popularity within the rating bracket."},
      { id: "", title: "Masters Play Differently", text : "While studying masters is a good thing to do, they respond to 1.e4 with 1...c5 (Sicilian Defense) more often that 1...e5. You need to practice in the same move ratio that you see in real games."},
      { id: "the_movelist", title: "The Game Universe", text : "The Lichess 'rapid' games were used to build the tree.<br><br>Note: the tree is still being filled out. It has 200M games now and will have 600M in a couple of months. After that about 16M games will be added every month."},
      { id: "the_movelist", title: "Move List", text : "Below the board will be the moves that have been made including each move's win/loss bar, popularity, and Stockfish engine score."},
      { id: "the_toolbar", title: "Flip - Toolbar", text : "If you are playing white, make a move and Chessvis will respond. You can, at any time, change the rating filter. If you flip the board and play black, Chessvis will make the first move."},
      { id: "the_toolbar", title: "Hint - Toolbar", text : "The Hint button will show you the most popular move you can make. Additional taps of the Hint button will step through the moves in descending popularity."},
      { id: "the_toolbar", title: "Rating Filter - Toolbar", text : "Different levels of players make different moves. It's important to filter the move tree to the bracket that you play in."},
    ]
  })

  const styleMoveList = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveHistory = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "left",
    "line-height" : "1.2em"
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    updateWidthHeights()
    getInitialUserInfo()
    menuTrigger.value++
  }

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleMoveList.value.height = boundaries['moveList'].height + "px"
    styleMoveList.value.width = boundaries['moveList'].width + "px"
    styleMoveList.value.top = boundaries['moveList'].top + "px"
    styleMoveList.value.left = boundaries['moveList'].left + "px"

    styleMoveHistory.value.height = boundaries['history'].height + "px"
    styleMoveHistory.value.width = boundaries['history'].width + "px"
    styleMoveHistory.value.top = boundaries['history'].top + "px"
    styleMoveHistory.value.left = boundaries['history'].left + "px"

    //console.log("**** boardsize ",boardSize.value)
  }

  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
}

function calculatePortraitWidthHeights(availableSpace) {

  // reserve space for move history and win/loss
  const availableSpaceForBoard = { width : availableSpace.width ,
                height : availableSpace.height * .67,
                isPortrait : true }

  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const historyPositionTop = boardPositionTop + boardHeight
  const historyPositionLeft = availableSpace.leftRightIndent
  const historyHeight = MOVEHISTORY_HEIGHT
  const historyWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const moveListPositionTop = historyPositionTop + historyHeight
  const moveListPositionLeft = availableSpace.leftRightIndent
  const moveListHeight = availableSpace.height - moveListPositionTop
  const moveListWidth =historyWidth

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft + availableSpace.leftIndent,
      top : boardPositionTop + availableSpace.topIndent
    },
    moveList : {
      width : moveListWidth,
      height : moveListHeight,
      left : moveListPositionLeft + availableSpace.leftIndent,
      top : moveListPositionTop + availableSpace.topIndent
    },
    history : {
      width : historyWidth,
      height : historyHeight,
      left : historyPositionLeft + availableSpace.leftIndent,
      top : historyPositionTop + availableSpace.topIndent
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

function calculateLandscapeWidthHeights(availableSpace) {

   // reserve space for the instruction lines
   const availableSpaceForBoard = { width : availableSpace.width - 200,
                height : availableSpace.height - MOVEHISTORY_HEIGHT,
                isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const moveListPositionTop = 0
  const moveListPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
  const moveListHeight = availableSpace.height - MOVEHISTORY_HEIGHT
  const moveListWidth = availableSpace.width - moveListPositionLeft

  const historyPositionTop = availableSpace.height  - MOVEHISTORY_HEIGHT
  const historyPositionLeft = availableSpace.leftRightIndent
  const historyHeight = MOVEHISTORY_HEIGHT
  const historyWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft + availableSpace.leftIndent,
      top : boardPositionTop + availableSpace.topIndent
    },
    moveList : {
      width : moveListWidth,
      height : moveListHeight,
      left : moveListPositionLeft + availableSpace.leftIndent,
      top : moveListPositionTop + availableSpace.topIndent
    },
    history : {
      width : historyWidth,
      height : historyHeight,
      left : historyPositionLeft + availableSpace.leftIndent,
      top : historyPositionTop + availableSpace.topIndent
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

  watch(() => windowSizeStore.getWindowSize, () => {
      //console.log('*********** Explorer -- window size changes detected')
      windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] }
      //console.log("*** Explorer new window size",windowSizeRef.value)
      updateWidthHeights()
      menuTrigger.value++
    })

  const initialFen = ref(INITIAL_FEN)
  const forceFen = ref(INITIAL_FEN)
  const hintFen = ref([])
  const animateFen = ref([])
  const eighty = ref(-3)

  var playingWhite = 1
  var orientation = ref("white")

  const availableMoves = ref([])
  var availablePlayerMoveStack = []         // stack of arrays of available moves at each player ply
  const validTos = ref([])
  const validFroms = ref([])
  const moveHistoryStack = ref([])
  const moveHistoryTrigger = ref(0)
  const moveHistoryString = ref("")
  const scrollToBottomTrigger = ref(true)
  const toolbarTrigger = ref(0)

  function tourDone() {
    showTour.value = false
  }

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function rewindClick() {
    noAvailableMovesFlag.value = 0
    incrementAppendTrigger()
    forceFen.value = initialFen.value + blankAppendTrigger
    moveHistoryStack.value = []
    moveHistoryTrigger.value++

    //console.log("toolbar rewind  playing white",playingWhite)
    if (playingWhite == 0) {
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,false)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,false)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,false)
      toolbarTrigger.value++
      getMoves(1)
    }
    else {
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,true)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,true)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,false)
      toolbarTrigger.value++
      getMoves(0)
    }
  }

  // START Generic Options List

  const openGenericSingleListModal = async () => {
    const modal = await modalController.create({
      component: ModalGenericSingleList,
      componentProps: {
        displayArray : optionDisplayArray,
        initialValue : optionDisplaySelected,
        title : t("Rating Filter")      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      // did user leave the select the same?
      if (data.selected == optionDisplaySelected)
        return

      //console.log("CONFIRM in play collection",data)

      ratingFilter = data.selected
      updateRating(ratingFilter)

   }
  };
  // END Generic Options List

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };
  
  function toolbarClick(index) {
    var newRatingFilter = ""

    // trap that the button should be disabled but the screen hasn't caught up
    var element = findToolbarElement(toolbarElements.value,index)
    if (element.disabled)
      return
      
    switch (index) {
      case TOOLBAR_RATING :
        optionDisplaySelected = ratingFilter
        openGenericSingleListModal()
        break
      case TOOLBAR_FLIP :
        incrementAppendTrigger()

        playingWhite = 1 - playingWhite
        orientation.value = (playingWhite == 1) ? "white" + blankAppendTrigger : "black" + blankAppendTrigger

        //console.log("flip",moveHistoryStack.value.length,playingWhite)
        if (moveHistoryStack.value.length <= 2) {
          rewindClick()
          break;
        }
        moveHistoryStack.value.pop()
        moveHistoryTrigger.value++
        forceFen.value = moveHistoryStack.value[moveHistoryStack.value.length - 1].fen + blankAppendTrigger
        getMoves(0)
        break;
      case TOOLBAR_BACK :
        if (moveHistoryStack.value.length <= 2) {
          rewindClick()
          break;
        }

        var numberToPop = 2

        // if white, need to see if have an odd # of entries (i.e., 1 loose opponent move)
        if (playingWhite == 1) {
          const isOdd = moveHistoryStack.value.length % 2
          numberToPop = (isOdd) ? 1 : 2
        }
        else {
          const isEven = 1 - moveHistoryStack.value.length % 2
          numberToPop = (isEven) ? 1 : 2
        }
        moveHistoryStack.value.pop()
        if (numberToPop == 2)
          moveHistoryStack.value.pop()

        moveHistoryTrigger.value++
        incrementAppendTrigger()
        forceFen.value = moveHistoryStack.value[moveHistoryStack.value.length - 1].fen + blankAppendTrigger
        noAvailableMovesFlag.value = 0

        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,false)

        toolbarTrigger.value++
        getMoves(0)
        break;
      case TOOLBAR_REWIND :
        rewindClick()
        break;
      case TOOLBAR_HINT :
        if (availableMoves.value.length == 0)
          break;
        var currentFen
        if (moveHistoryStack.value.length)
          currentFen = moveHistoryStack.value[moveHistoryStack.value.length-1].fen
        else
          currentFen = INITIAL_FEN
        hintFen.value = [currentFen, availableMoves.value[hintIndex].fen]
        //animateFen.value = [fen.value, availableMoves.value[hintIndex].fen]
        hintIndex++
        if (hintIndex == availableMoves.value.length)
          hintIndex = 0
        break
    }
  }

  function boardMove(arrayIndex,from,to) {
    //console.log("board move ",arguments)

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_BACK,false)
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_REWIND,false)

    toolbarTrigger.value++
    moveHistoryStack.value.push(availableMoves.value[arrayIndex])
    availablePlayerMoveStack.push(availableMoves.value)
    getMoves(1)
  }

  function processPlayerJsonResponse (json) {
    //console.log(" in Player callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FLIP,false)
    toolbarTrigger.value++

    const count = json['count']
    if (count == 0) {
      availableMoves.value = []
      noAvailableMovesFlag.value = 2
      moveHistoryTrigger.value++
      scrollToBottomTrigger.value = !scrollToBottomTrigger.value

      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)

      toolbarTrigger.value++
      return
    }

    const whiteString = json['white']
    const blackString = json['black']
    const whites = whiteString.split("\t")
    const blacks = blackString.split("\t")
    const sans = json['san'].split("\t")
    const totals = json['total'].split("\t")
    const scores = json['score'].split("\t")
    const fens = json['fen'].split("\t")
    const fenid4s = json['fenid4'].split("\t")
    const froms = json['from'].split("\t")
    const tos = json['to'].split("\t")

    availableMoves.value = []

    const moveNumber = Math.floor(moveHistoryStack.value.length / 2) + 1
    var sanPadding = ""
    if (moveHistoryStack.value.length % 2)
      sanPadding = (moveNumber < 10) ? "  " : "   "
    else
      sanPadding = moveNumber + "."

    for (var i=0; i < count; i++) {
      const oneMove = { index: i, fenid: fenid4s[i], white : whites[i], black : blacks[i], 
                        draw : "0", from : froms[i], to: tos[i],
                        san : sanPadding + sans[i], total : totals[i], score : scores[i], fen : fens[i]}
      availableMoves.value.push(oneMove)
    }
    scrollToBottomTrigger.value = !scrollToBottomTrigger.value
  }

  function processOpponentJsonResponse (json) {
    //console.log(" in Opponent callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FLIP,false)
    toolbarTrigger.value++

    const count = json['count']

    if (count == 0) {
      availableMoves.value = []
      noAvailableMovesFlag.value = 1
      moveHistoryTrigger.value++
      scrollToBottomTrigger.value = !scrollToBottomTrigger.value

      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)

      toolbarTrigger.value++
      return
    }
    const white = json['white']
    const black = json['black']
    const san = json['san']
    const total = json['total']
    const score = json['score']
    const fen = json['fen']
    const fenid4 = json['fenid4']
    const from = json['from']

    var tempData = []

    const moveNumber = Math.floor(moveHistoryStack.value.length / 2) + 1
    var sanPadding = ""
    if (moveHistoryStack.value.length % 2)
      sanPadding = (moveNumber < 10) ? "  " : "   "
    else
      sanPadding = moveNumber + "."

    const oneMove = { index: 0, fenid: fenid4, white : white, black : black, 
                        draw : "0", from : from,
                        san : sanPadding + san, total : total, score : score, fen : fen}

    const currentFen = (moveHistoryStack.value.length) ? 
              moveHistoryStack.value[moveHistoryStack.value.length-1].fen : INITIAL_FEN
    moveHistoryStack.value.push(oneMove)
    moveHistoryTrigger.value++
    animateFen.value = [currentFen, fen]

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,false)

    toolbarTrigger.value++
    getMoves(0)
  }

  function getMoves(pickOneMove) {

    // disable flip while getting move
    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_FLIP,true)

    const moveMadeWasForColor = (pickOneMove) ? 1 - playingWhite : playingWhite
    var fenid8 = initialFenid8
    var fenid4 = initialFenid4

    if (moveHistoryStack.value.length)
      fenid4 = moveHistoryStack.value[moveHistoryStack.value.length-1].fenid
    var formData = {
      opponent : fenid4,
      pickone : pickOneMove,
      white : moveMadeWasForColor,
      ply : moveHistoryStack.value.length
    }

    //console.log("before post -> ",formData)

    hintIndex = 0

    useGetData("chessvis_legacy_play_against_collection.php", formData, 
            (pickOneMove) ? processOpponentJsonResponse : processPlayerJsonResponse)
  }

  function processMoveCountJsonResponse (json) {
    //console.log(" in move count callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const whites = json['white'].split("\t")
    const blacks = json['black'].split("\t")
    const totals = json['total'].split("\t")

    for (var i=0; i < moveHistoryStack.value.length; i++) {
      moveHistoryStack.value[i].white = whites[i]
      moveHistoryStack.value[i].black = blacks[i]
      moveHistoryStack.value[i].total = totals[i]
    }
    scrollToBottomTrigger.value = !scrollToBottomTrigger.value

    getMoves(0)
  }

  function getMoveCounts(fenidsString) {
    var formData = {
      fenid4s : fenidsString
    }

    //console.log("before post to update move counts -> ",formData)

    useGetData("chessvis_legacy_play_update_move_counts.php", formData, processMoveCountJsonResponse)
  }
 
  function processInitialJsonResponse (json) {
    //console.log(" in initial json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    ratingFilter = json['rating_filter']
    if (ratingFilter == "" || ratingFilter == "0")
      ratingFilter = "all"

    getMoves(0)

  }

  function getInitialUserInfo() {
    var formData = {}
    useGetData("chessvis_legacy_user_explorer_tree_info.php", formData, processInitialJsonResponse)
  }


  function processRatingUpdateJsonResponse(json) {
    //console.log(" in set rating callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    // get the new counts based on the rating change

    // build list of fenids so we can updated game collection counts
    var fenids = []
    for (var i=0; i < moveHistoryStack.value.length; i++)
      fenids.push(moveHistoryStack.value[i].fenid)
    const fenidsString = fenids.join(",")
    //console.log("*** Fenid string ",fenidsString)
    getMoveCounts(fenidsString)
  }

  function updateRating(newRating) {
    var formData = {
      rating : newRating
    }
    //console.log("before post -> ",formData)

    useGetData("chessvis_legacy_set_rating_filter.php", formData, processRatingUpdateJsonResponse)
  }
</script>

<style scoped>

#explorer-container {
  position : relative;
}
</style>